'use client';

import Image from 'next/image';
import { useRef, Fragment, useState } from 'react';
import { AnimatePresence, easeIn, easeInOut, easeOut, motion, useScroll, useTransform } from 'framer-motion';
import { PortableTextBlock } from 'sanity';
import { PortableText } from 'next-sanity';
import { richTextComponent, customRichTextComponent } from '@/sanity/sanityPortableText';
import { useIsMobile } from '@/hooks/useIsMobile';
import ChipText from '@/components/global/ChipText';
import { GROQImage } from "@/sanity/lib/types";
import { GROQButton } from '@/sanity/lib/definitions';
import { CTAButton } from '@/shadcn/ui/button';
import { cn } from '@/lib/utils';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/shadcn/ui/carousel';

interface IPackageProps {
  chipText: string;
  title: PortableTextBlock[];
  packageMonthly: {
    title: string;
    packages: {
      type: 'Starter' | 'Pro' | 'Scale' | 'Pay per Lead';
      price: string;
      description: PortableTextBlock[];
      button: GROQButton;
    }[];
  };
  packageQuarterly: {
    title: string;
    packages: {
      type: 'Starter' | 'Pro' | 'Scale' | 'Pay per Lead';
      price: string;
      description: PortableTextBlock[];
      button: GROQButton;
    }[];
  };
  packageSixMonths: {
    title: string;
    packages: {
      type: 'Starter' | 'Pro' | 'Scale' | 'Pay per Lead';
      price: string;
      description: PortableTextBlock[];
      button: GROQButton;
    }[];
  };
  totalButton: GROQButton;
}

export default function Package({
  chipText,
  title,
  packageMonthly,
  packageQuarterly,
  packageSixMonths,
  totalButton,
}: IPackageProps) {
  const isMobile = useIsMobile();
  const [activePeriod, setActivePeriod] = useState(0); // 0: Monthly, 1: Quarterly, 2: Six Months
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [selectedPackages, setSelectedPackages] = useState<Array<{
    type: string;
    price: string;
    period: string;
  }>>([]);
  const [isTableExpanded, setIsTableExpanded] = useState(true);

  const periods = [
    { label: packageMonthly?.title, packages: packageMonthly?.packages || [] },
    { label: packageQuarterly?.title, packages: packageQuarterly?.packages || [] },
    { label: packageSixMonths?.title, packages: packageSixMonths?.packages || [] },
  ];

  const currentPackages = periods[activePeriod].packages;

  const togglePackageSelection = (pkg: any) => {
    const packageId = `${periods[activePeriod].label}-${pkg.type}`;
    const isSelected = selectedPackages.some(
      (p) => `${p.period}-${p.type}` === packageId
    );

    if (isSelected) {
      setSelectedPackages(
        selectedPackages.filter((p) => `${p.period}-${p.type}` !== packageId)
      );
    } else {
      setSelectedPackages([
        ...selectedPackages,
        {
          type: pkg.type,
          price: pkg.price,
          period: periods[activePeriod].label,
        },
      ]);
    }
  };

  const isPackageSelected = (pkg: any) => {
    const packageId = `${periods[activePeriod].label}-${pkg.type}`;
    return selectedPackages.some(
      (p) => `${p.period}-${p.type}` === packageId
    );
  };

  const calculateTotal = () => {
    return selectedPackages.reduce((total, pkg) => {
      const price = parseFloat(pkg.price.replace(/[^0-9.-]+/g, ''));
      return total + (isNaN(price) ? 0 : price);
    }, 0);
  };

  const formatType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatCurrency = (value: number) => {
    // Format number with space as thousands separator and comma as decimal separator
    const formattedNumber = value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ').replace('.', ',');
    return `€ ${formattedNumber}`;
  };

  return (
    <section className="pt-[8rem] lg:pt-[11.5rem] pb-[5rem] lg:pb-[10rem] w-full">
      <div className="container px-[2rem] lg:px-0 flex flex-col items-center gap-[4rem] lg:gap-[5.33rem] relative">
        <div className="flex flex-col gap-[2rem] w-full items-center">
          {chipText &&
            <ChipText text={chipText} color="purple" />
          }
          <div className="w-full items-center richtext richtext-center prose-headings:text-[4rem] lg:prose-headings:text-[6rem] prose-headings:mb-0">
            {title && <PortableText value={title} components={richTextComponent} />}
          </div>
        </div>

        {/* Period Filter */}
        {isMobile ? (
          <Carousel
            setApi={setCarouselApi}
            opts={{
              align: "start",
              loop: false,
            }}
            className="w-full"
          >
            <CarouselContent>
              {periods.map((period, index) => {
                const isActive = activePeriod === index;
                return (
                  <CarouselItem key={index} className="basis-auto pl-0 bg-[#E8E6D6] p-[1rem] first:pr-0 last:pl-0 rounded-[.25rem]">
                    <button
                      onClick={() => {
                        setActivePeriod(index);
                      }}
                      className={cn(
                        "flex flex-row gap-[1.25rem] items-center justify-center py-[2rem] px-[3rem] h-[8rem] rounded-[.25rem] transition-all duration-300 cursor-pointer",
                        isActive ? "bg-[#072922]" : "bg-transparent"
                      )}
                    >
                      <span className={cn(
                        "text-[1.75rem] font-[400] leading-none font-inter-tight transition-colors duration-300 whitespace-nowrap",
                        isActive ? "text-green" : "text-[#908C6B]"
                      )}>
                        {period.label}
                      </span>

                      {index !== 0 && (
                        <div className="flex flex-row items-center gap-[1.25rem] bg-green p-[.5rem] rounded-[.25rem]">
                          <svg className="w-[3rem] h-[3rem] flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M21 4.5C21.3978 4.5 21.7792 4.65815 22.0605 4.93945C22.3419 5.22076 22.5 5.60218 22.5 6V9.75C22.5 9.94891 22.4209 10.1396 22.2803 10.2803C22.1396 10.4209 21.9489 10.5 21.75 10.5C21.3522 10.5 20.9708 10.6581 20.6895 10.9395C20.4081 11.2208 20.25 11.6022 20.25 12C20.25 12.3978 20.4081 12.7792 20.6895 13.0605C20.9708 13.3419 21.3522 13.5 21.75 13.5C21.9489 13.5 22.1396 13.5791 22.2803 13.7197C22.4209 13.8604 22.5 14.0511 22.5 14.25V18C22.5 18.3978 22.3419 18.7792 22.0605 19.0605C21.7792 19.3419 21.3978 19.5 21 19.5H3C2.60218 19.5 2.22076 19.3419 1.93945 19.0605C1.65815 18.7792 1.5 18.3978 1.5 18V14.25C1.5 14.0511 1.57907 13.8604 1.71973 13.7197C1.86038 13.5791 2.05109 13.5 2.25 13.5C2.64782 13.5 3.02924 13.3419 3.31055 13.0605C3.59185 12.7792 3.75 12.3978 3.75 12C3.75 11.6022 3.59185 11.2208 3.31055 10.9395C3.02924 10.6581 2.64782 10.5 2.25 10.5C2.05109 10.5 1.86038 10.4209 1.71973 10.2803C1.57907 10.1396 1.5 9.94891 1.5 9.75V6C1.5 5.60218 1.65815 5.22076 1.93945 4.93945C2.22076 4.65815 2.60218 4.5 3 4.5H21ZM3 6V9.09766C3.64196 9.26545 4.21065 9.64086 4.61621 10.166C5.02178 10.6912 5.24121 11.3364 5.24121 12C5.24121 12.6636 5.02178 13.3088 4.61621 13.834C4.21065 14.3591 3.64196 14.7345 3 14.9023V18H14.25V15.75H15.75V18H21V14.9023C20.358 14.7345 19.7893 14.3591 19.3838 13.834C18.9782 13.3088 18.7588 12.6636 18.7588 12C18.7588 11.3364 18.9782 10.6912 19.3838 10.166C19.7893 9.64086 20.358 9.26545 21 9.09766V6H15.75V8.25H14.25V6H3ZM15.75 9.75V14.25H14.25V9.75H15.75Z" fill="#011410"/>
                          </svg>
                          <span className="font-inter-tight text-[1.75rem] font-[500] leading-none text-fakeblack whitespace-nowrap">
                            {index === 1 ? "5" : "10"}% OFF
                          </span>
                        </div>
                      )}
                    </button>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
          </Carousel>
        ) : (
          <div className="flex flex-row items-center bg-[#E8E6D6] p-[1rem] rounded-[.25rem]">
            {periods.map((period, index) => {
              const isActive = activePeriod === index;
              return (
                <button
                  key={index}
                  onClick={() => {
                    setActivePeriod(index);
                  }}
                  className={cn(
                    "flex flex-row gap-[1.25rem] items-center justify-center py-[2rem] px-[3rem] w-[37.5rem] h-[8rem] rounded-[.25rem] transition-all duration-300 cursor-pointer",
                    isActive ? "bg-[#072922]" : "bg-transparent"
                  )}
                >
                  <span className={cn(
                    "text-[2.5rem] font-[400] leading-[140%] font-inter-tight transition-colors duration-300",
                    isActive ? "text-green" : "text-[#908C6B]"
                  )}>
                    {period.label}
                  </span>

                  {index !== 0 && (
                    <div className="flex flex-row items-center gap-[1.25rem] bg-green p-[.5rem] rounded-[.25rem]">
                      <svg className="w-[3rem] h-[3rem] flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M21 4.5C21.3978 4.5 21.7792 4.65815 22.0605 4.93945C22.3419 5.22076 22.5 5.60218 22.5 6V9.75C22.5 9.94891 22.4209 10.1396 22.2803 10.2803C22.1396 10.4209 21.9489 10.5 21.75 10.5C21.3522 10.5 20.9708 10.6581 20.6895 10.9395C20.4081 11.2208 20.25 11.6022 20.25 12C20.25 12.3978 20.4081 12.7792 20.6895 13.0605C20.9708 13.3419 21.3522 13.5 21.75 13.5C21.9489 13.5 22.1396 13.5791 22.2803 13.7197C22.4209 13.8604 22.5 14.0511 22.5 14.25V18C22.5 18.3978 22.3419 18.7792 22.0605 19.0605C21.7792 19.3419 21.3978 19.5 21 19.5H3C2.60218 19.5 2.22076 19.3419 1.93945 19.0605C1.65815 18.7792 1.5 18.3978 1.5 18V14.25C1.5 14.0511 1.57907 13.8604 1.71973 13.7197C1.86038 13.5791 2.05109 13.5 2.25 13.5C2.64782 13.5 3.02924 13.3419 3.31055 13.0605C3.59185 12.7792 3.75 12.3978 3.75 12C3.75 11.6022 3.59185 11.2208 3.31055 10.9395C3.02924 10.6581 2.64782 10.5 2.25 10.5C2.05109 10.5 1.86038 10.4209 1.71973 10.2803C1.57907 10.1396 1.5 9.94891 1.5 9.75V6C1.5 5.60218 1.65815 5.22076 1.93945 4.93945C2.22076 4.65815 2.60218 4.5 3 4.5H21ZM3 6V9.09766C3.64196 9.26545 4.21065 9.64086 4.61621 10.166C5.02178 10.6912 5.24121 11.3364 5.24121 12C5.24121 12.6636 5.02178 13.3088 4.61621 13.834C4.21065 14.3591 3.64196 14.7345 3 14.9023V18H14.25V15.75H15.75V18H21V14.9023C20.358 14.7345 19.7893 14.3591 19.3838 13.834C18.9782 13.3088 18.7588 12.6636 18.7588 12C18.7588 11.3364 18.9782 10.6912 19.3838 10.166C19.7893 9.64086 20.358 9.26545 21 9.09766V6H15.75V8.25H14.25V6H3ZM15.75 9.75V14.25H14.25V9.75H15.75Z" fill="#011410"/>
                      </svg>
                      <span className="font-inter-tight text-[2rem] font-[500] leading-none text-fakeblack">
                        {index === 1 ? "5" : "10"}% OFF
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Package Cards */}
        <div className="w-[min(175rem,100%)] mx-auto flex lg:flex-row flex-col lg:gap-[3rem] gap-[4.35rem]">
          {currentPackages?.map((pkg, index) => {
            const type = pkg.type;
            return (
              <div key={`${activePeriod}-${index}`} className="flex flex-col">
                {type === "Pro" && (
                  <div className="flex flex-row gap-[1.25rem] self-end items-center bg-[#072922] rounded-[.25rem] py-[1rem] px-[1.75rem] mb-[-3rem] mr-[4rem] relative z-10">
                      <svg className="lg:w-[3rem] lg:h-[3rem] w-[2.5rem] h-[2.5rem] flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                        <g clip-path="url(#clip0_521_1718)">
                          <path d="M12 4.89L14.07 9.075L14.415 9.825L15.165 9.9375L19.785 10.605L16.5 13.83L15.9375 14.3775L16.0725 15.1275L16.86 19.725L12.7275 17.5575L12 17.25L11.3025 17.6175L7.16996 19.755L7.91996 15.1575L8.05496 14.4075L7.49996 13.83L4.18496 10.5675L8.80496 9.9L9.55496 9.7875L9.89996 9.0375L12 4.89ZM12 1.5L8.58746 8.415L0.959961 9.5175L6.47996 14.9025L5.17496 22.5L12 18.915L18.825 22.5L17.52 14.9025L23.04 9.525L15.4125 8.415L12 1.5Z" fill="#B0F4B7" />
                        </g>
                        <defs>
                          <clipPath id="clip0_521_1718">
                            <rect width="24" height="24" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>

                      <span className="font-inter-tight text-[2rem] lg:text-[2.5rem] font-[400] leading-[95%] text-green">
                        Recommended
                      </span>
                  </div>
                )}

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{
                    ease: easeInOut,
                    duration: 0.4,
                    delay: index * 0.1,
                  }}
                  className={cn(
                    "flex flex-col gap-[3rem] w-full px-[2rem] lg:px-[3rem] pt-[3rem] pb-[6.44rem] rounded-[.5rem] relative",
                    type !== "Pro" && "lg:mt-[8rem]"
                  )}
                >
                  {type !== "Pro" ? (
                    <svg className="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 399 701" fill="none" preserveAspectRatio="none">
                      <path d="M398.5 626.816C398.5 627.966 398.006 629.059 397.143 629.819L317.97 699.503C317.239 700.145 316.3 700.5 315.327 700.5L4.5017 700.5C2.29257 700.5 0.5017 698.709 0.501695 696.5L0.500035 4.49999C0.50003 2.29085 2.29089 0.499983 4.50004 0.499983L394.499 0.500058C396.708 0.500058 398.499 2.29092 398.499 4.50005L398.5 626.816Z" stroke="#D5D2B2" strokeWidth="1" fill="none"/>
                    </svg>
                  ) : (
                    <svg className="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 398 773" fill="none" preserveAspectRatio="none">
                      <path d="M398 691.938C398 693.019 397.563 694.053 396.788 694.807L317.501 771.868C316.754 772.594 315.754 773 314.713 773L4.00171 773C1.79257 773 0.00170579 771.209 0.00170589 769L3.03427e-05 3.99998C3.04393e-05 1.79083 1.79089 -1.70268e-05 4.00003 -1.65699e-05L393.999 6.40811e-05C396.208 6.4538e-05 397.999 1.79092 397.999 4.00006L398 691.938Z" fill="#B0F4B7"/>
                    </svg>
                  )}
                  
                  {/* Price */}
                  <div className="flex flex-col gap-[2rem] relative z-10">
                    <span
                      className="block font-inter-tight font-[400] text-[2.5rem] leading-none uppercase">
                      {formatType(pkg.type)}
                    </span>

                    <div className="flex flex-row lg:flex-col items-end lg:items-start gap-[1rem] lg:gap-0">
                      {pkg.price && (
                        <p className="font-inter-tight text-[5rem] font-[500] leading-none">
                          {pkg.price}
                        </p>
                      )}
                      <span className="font-inter-tight text-[2rem] lg:text-[2.5rem] font-[400] leading-[140%]">
                        {periods[activePeriod].label === packageMonthly.title ? "/month" : periods[activePeriod].label === packageQuarterly.title ? "/quarter" : "/6 months"}
                      </span>
                    </div>

                    {pkg.type === 'Pay per Lead' && pkg.button ? (
                      <CTAButton
                        data={pkg.button}
                        className="w-full lg:w-auto"
                      />
                    ) : (
                      <button
                        onClick={() => togglePackageSelection(pkg)}
                        className={cn(
                          "w-full lg:w-auto px-[3rem] py-[2rem] rounded-[.5rem] font-inter-tight text-[2rem] font-[500] transition-all duration-300",
                          isPackageSelected(pkg)
                            ? "bg-fakeblack text-green hover:bg-fakeblack/80"
                            : "bg-purple text-white"
                        )}
                      >
                        {isPackageSelected(pkg) ? "Selected" : "Select"}
                      </button>
                    )}
                  </div>

                  {/* Divider */}
                  <div
                    className={cn(
                      "w-full h-[.125rem] relative z-10",
                      type === "Pro"
                        ? "bg-[#072922]/[24%]"
                        : "bg-[#D5D2B2]"
                    )}
                  />

                  {/* Topics List */}
                  <div className="flex flex-col gap-[1.20rem] prose-strong:font-[700] relative z-10">
                    <PortableText value={pkg.description} components={customRichTextComponent({
                      block: {
                        normal: ({ children }) => (
                          <p className="font-inter-tight text-[1.75rem] font-[400] leading-[120%]">{children}</p>
                        ),
                      },
                      list: {
                        bullet: ({ children }) => (
                          <ul className="flex flex-col gap-[1rem] list-none pl-0 mb-[1.80rem]">
                            {children}
                          </ul>
                        ),
                      },
                      listItem: {
                        bullet: ({ children }) => (
                          <li className="flex items-start gap-[1rem]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 16 16"
                              fill="none"
                              className="w-[2rem] h-[2rem] flex-shrink-0"
                            >
                              <path d="M6.5 12L2 7.49997L2.707 6.79297L6.5 10.5855L13.293 3.79297L14 4.49997L6.5 12Z" fill="#011410"/>
                            </svg>
                            <span className="font-inter-tight text-[1.75rem] font-[400] leading-[120%]">{children}</span>
                          </li>
                        ),
                      },
                    })} />
                  </div>
                </motion.div>
              </div>
            );
          })}
        </div>

        {/* Price Calculator */}
        <div className="flex flex-col items-center justify-center gap-[4rem] lg:gap-[7.25rem] w-full max-w-[137.375rem] mx-auto mt-[2rem] lg:mt-[6rem]">
          <div className="flex flex-col rounded-[.5rem] overflow-hidden border border-[#D5D2B2] w-full">
            {/* Header */}
            <div className="bg-[#072922] px-[3rem] py-[3rem] flex flex-row items-center justify-between">
              <span className="font-inter-tight text-[2rem] lg:text-[2.5rem] font-[400] text-green max-w-[20.77488rem] lg:max-w-none">
                Selected Packages
              </span>
              <div className="flex flex-row items-center gap-[3rem]">
                <button onClick={() => setIsTableExpanded(!isTableExpanded)}>
                  <svg
                    className={cn(
                      "w-[3rem] h-[3rem] transition-transform duration-300",
                      !isTableExpanded && "rotate-180"
                    )}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path d="M12 16.5002L4.5 9.0002L5.55 7.9502L12 14.4002L18.45 7.9502L19.5 9.0002L12 16.5002Z" fill="#B0F4B7"/>
                  </svg>
                </button>
                <div className="flex flex-col items-end gap-[.5rem]">
                  <span className="font-inter-tight text-[1.5rem] font-[400] text-green/40 leading-none text-right uppercase">
                    Total:
                  </span>
                  <span className="font-inter-tight text-[2rem] lg:text-[2.5rem] font-[500] text-green text-right">
                    {formatCurrency(calculateTotal())}
                  </span>
                </div>
              </div>
            </div>

            {/* Table */}
            <AnimatePresence initial={false}>
              {isTableExpanded && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                  exit={{ height: 0 }}
                  transition={{
                    duration: 0.4,
                    ease: easeInOut,
                  }}
                  className="overflow-hidden"
                >
                  {/* Table Header */}
                  <div className="flex flex-row justify-between gap-[2rem] p-[3rem]">
                    <span className="font-inter-tight text-[1.75rem] font-[500] text-[#072922]">
                      Description
                    </span>
                    <div className="grid grid-cols-2 items-end w-1/2 lg:w-[30%] gap-[2rem] lg:gap-0">
                      <span className="font-inter-tight text-[1.75rem] font-[500] text-[#072922] lg:text-right">
                        Item
                      </span>
                      <span className="font-inter-tight text-[1.75rem] font-[500] text-[#072922] lg:text-right">
                        Price
                      </span>
                    </div>
                  </div>

                  {/* Table Rows */}
                  {selectedPackages.map((pkg, index) => (
                    <div
                      key={index}
                      className="flex flex-row justify-between gap-[2rem] p-[3rem]"
                    >
                      <span className="font-inter-tight text-[1.75rem] font-[500] text-[#072922]">
                        {pkg.type} - {pkg.period}
                      </span>
                      <div className="grid grid-cols-2 items-end w-1/2 lg:w-[30%] gap-[2rem] lg:gap-0">
                        <span className="font-inter-tight text-[1.75rem] font-[500] text-[#072922] lg:text-right">
                          {pkg.price}
                        </span>
                        <span className="font-inter-tight text-[1.75rem] font-[500] text-[#072922] lg:text-right">
                          {pkg.price}
                        </span>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Total */}
          {totalButton && (
            <CTAButton
              data={{
                ...totalButton,
                cta: totalButton.cta ? {
                  ...totalButton.cta,
                  title: `Total: ${formatCurrency(calculateTotal())}`,
                } : undefined,
              }}
              className="!w-full lg:!max-w-[42rem]"
            />
          )}
        </div>
      </div>
    </section>
  );
}
